import { HAND_SPEED_SENSOR_TYPE, type Sensor } from "@arc/contracts";

/**
 * The pure model behind the Manage sensors page (ADR-035 D6) — the legacy's two tabs and its list order, with the two
 * legacy bugs left behind.
 */

/** The legacy's exactly-two tabs; there is no "all". */
export type SensorTab = "sensors" | "handSpeed";
/** Tab-label KEYS. This module is pure classification logic and has no translator of its own — and the legacy
 *  stored the TRANSLATED label as the selected tab, which is exactly the bug the comment below describes. */
export const SENSOR_TAB_LABEL_KEY = {
  sensors: "sensors.sensorButtonText",
  handSpeed: "sensors.sensorStateHand",
} as const satisfies Record<SensorTab, string>;

/**
 * Which tab a sensor belongs to. The legacy matched the substring "hand" on the type AND stored the selected tab as
 * the TRANSLATED label (so a language switch broke the filter, `ARLMS_SensorManagement.js:7,121,137`). This keys off
 * the type VALUE against the known hand-speed type, and treats an unknown type as a plain sensor — a new device
 * should show up somewhere, never vanish.
 */
export function tabOf(sensor: Sensor): SensorTab {
  return sensor.type === HAND_SPEED_SENSOR_TYPE ? "handSpeed" : "sensors";
}

/** The label the list and the detail heading show — the legacy's `displayName || name`. */
export function sensorLabel(sensor: Sensor): string {
  return sensor.displayName?.trim() || sensor.name;
}

/**
 * Natural order over the slug, so `T2` sorts before `T10`. The legacy reached for this with
 * `Math.floor(slug.substring(1,3))` — arithmetic on two characters, which mis-sorts anything that isn't a
 * one-letter + two-digit code and collapses `T1`/`T1x`. Ties fall back to the whole slug, then the id (stable).
 */
export function compareSensors(a: Sensor, b: Sensor): number {
  return a.slug.localeCompare(b.slug, undefined, { numeric: true, sensitivity: "base" }) || a.slug.localeCompare(b.slug) || a.id - b.id;
}

/** Case-insensitive substring over what the operator can SEE: the slug, the label, and the technical name. */
export function matchesSensorFilter(sensor: Sensor, term: string): boolean {
  const q = term.trim().toLocaleLowerCase();
  if (q === "") return true;
  return [sensor.slug, sensorLabel(sensor), sensor.name].some((v) => v.toLocaleLowerCase().includes(q));
}

/** The tab's rows: filtered, then natural-sorted. */
export function sensorsForTab(sensors: readonly Sensor[], tab: SensorTab, term: string): Sensor[] {
  return sensors.filter((s) => tabOf(s) === tab && matchesSensorFilter(s, term)).sort(compareSensors);
}

/** The tab counts — computed over the FILTER too, so a filtered-away tab reads 0 rather than lying. */
export function sensorTabCounts(sensors: readonly Sensor[], term: string): Record<SensorTab, number> {
  return {
    sensors: sensors.filter((s) => tabOf(s) === "sensors" && matchesSensorFilter(s, term)).length,
    handSpeed: sensors.filter((s) => tabOf(s) === "handSpeed" && matchesSensorFilter(s, term)).length,
  };
}
